import axios from 'axios';

let logoutCallback: ((reason?: string) => void) | null = null;
// Guard to prevent duplicate logout/toast when multiple 401s fire at once
let hasHandledTokenExpiry = false;

export function setupAuthInterceptor(logout: (reason?: string) => void) {
  logoutCallback = logout;
}

function handleTokenExpired() {
  if (hasHandledTokenExpiry) {
    // Already handled this expiry; avoid duplicate toasts/logouts
    return;
  }
  hasHandledTokenExpiry = true;
  if (logoutCallback) {
    console.log('🔒 Token has expired - forcing logout (deduped)');
    // Pass a reason so the UI can show a toast
    logoutCallback('expired');
  }
}

// Optional: expose a reset in case app wants to clear dedup state after re-auth
export function resetAuthInterceptorDedup() {
  hasHandledTokenExpiry = false;
}

// Check if a 401 error is specifically due to token expiration
function isTokenExpiredError(errorData: any): boolean {
  // Check for the specific "Token has expired" message from backend
  if (errorData?.detail === "Token has expired") {
    return true;
  }
  
  // Also check for the error field (worker-api format)
  if (errorData?.error === "Token has expired") {
    return true;
  }
  
  // Check WWW-Authenticate header for expired token indication
  if (errorData?.headers?.['www-authenticate']?.includes('invalid_token')) {
    return true;
  }
  
  return false;
}

// Setup axios interceptor for 401 responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only force logout if it's specifically a token expiration error
      if (isTokenExpiredError(error.response.data)) {
        handleTokenExpired();
      } else {
        // For other 401 errors, just log them
        console.warn('401 Unauthorized (not token expiration):', error.response.data);
      }
    }
    return Promise.reject(error);
  }
);

// Enhanced debugging storage
interface DebugEntry {
  timestamp: string;
  method: string;
  url: string;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  requestBody?: any;
  responseBody?: any;
  error?: string;
  duration: number;
}

// Store last 20 requests for debugging
const debugHistory: DebugEntry[] = [];
const MAX_DEBUG_ENTRIES = 20;

// Export debug history for external access
(window as any).__DEBUG_HISTORY__ = debugHistory;
(window as any).__CLEAR_DEBUG__ = () => { debugHistory.length = 0; };
(window as any).__SHOW_DEBUG__ = () => console.table(debugHistory);

// Global fetch wrapper that handles 401 responses
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const startTime = Date.now();
  const requestUrl = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
  const requestMethod = (args[1]?.method || (args[0] as Request).method || 'GET').toUpperCase();
  
  // Capture request details for debugging
  let requestBody: any = undefined;
  try {
    if (args[1]?.body) {
      if (typeof args[1].body === 'string') {
        requestBody = JSON.parse(args[1].body);
      } else {
        requestBody = args[1].body;
      }
    }
  } catch (e) {
    requestBody = args[1]?.body;
  }
  
  // Log request start with enhanced details
  console.group(`🔄 ${requestMethod} ${requestUrl}`);
  console.log('Request started at:', new Date().toISOString());
  if (requestBody) {
    console.log('Request body:', requestBody);
  }
  if (args[1]?.headers) {
    console.log('Request headers:', args[1].headers);
  }
  
  let response: Response;
  let responseBody: any = undefined;
  let errorMessage: string | undefined;
  
  try {
    response = await originalFetch(...args);

    // Log response details
    console.log(`Response status: ${response.status} ${response.statusText}`);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    // Detect Server-Sent Events (SSE) responses so we don't consume the body
    const contentType = response.headers.get('content-type') || '';
    const isSSEContentType = contentType.toLowerCase().includes('text/event-stream');
    // Also check request intent for streaming
    const reqHeaders: any = (args[1] as any)?.headers;
    const acceptHeader = reqHeaders?.get ? reqHeaders.get('Accept') : (reqHeaders?.Accept || reqHeaders?.accept);
    const isSSERequest = typeof requestUrl === 'string' && /\/stream\//.test(requestUrl) || (acceptHeader && String(acceptHeader).toLowerCase().includes('text/event-stream'));
    const isSSE = isSSEContentType || isSSERequest;

    // Special handling for 500 errors (skip body read for SSE to avoid blocking streaming)
    if (response.status === 500) {
      console.error('🚨 500 Internal Server Error detected!');

      if (!isSSE) {
        // Try to get error details without impacting streaming
        try {
          const clonedResponse = response.clone();
          responseBody = await clonedResponse.text();

          // Try to parse as JSON
          try {
            responseBody = JSON.parse(responseBody);
            console.error('Error response body:', responseBody);
          } catch {
            console.error('Error response (text):', responseBody);
          }

          // Store detailed error info
          errorMessage = `500 Error: ${responseBody?.error || responseBody?.message || responseBody || 'Unknown error'}`;
        } catch (e) {
          console.error('Could not read error response:', e);
          errorMessage = '500 Error: Could not read response body';
        }
      } else {
        console.warn('Skipping error body read for SSE response');
      }
    }

    // Check for 401 status
    if (response.status === 401) {
      // Don't process auth endpoints
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as any)?.url;
      const isAuthEndpoint = url && (
        url.includes('/auth/token') || 
        url.includes('/auth/register') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/logout')
      );
      
      if (!isAuthEndpoint) {
        // Try to parse the response body to check if it's token expiration
        try {
          const clonedResponse = response.clone();
          const errorData = await clonedResponse.json();
          responseBody = errorData;
          
          if (isTokenExpiredError(errorData)) {
            handleTokenExpired();
          } else {
            console.warn('401 Unauthorized (not token expiration):', errorData);
          }
        } catch (e) {
          // If we can't parse the response, just log it
          console.warn('401 Unauthorized (could not parse response)');
        }
      }
    }
    
    // Try to capture response body for other statuses (but NEVER for SSE)
    if (!responseBody && response.status !== 204 && !isSSE) {
      try {
        const clonedResponse = response.clone();
        const ct = response.headers.get('content-type');
        if (ct?.includes('application/json')) {
          responseBody = await clonedResponse.json();
        } else {
          responseBody = await clonedResponse.text();
        }
      } catch (e) {
        // Ignore body read errors
      }
    }
    
  } catch (error) {
    // Determine if this request is intended for SSE/streaming
    const reqUrl = typeof args[0] === 'string' ? args[0] : (args[0] as any)?.url;
    const reqHeaders: any = (args[1] as any)?.headers;
    const acceptHeader = reqHeaders?.get ? reqHeaders.get('Accept') : (reqHeaders?.Accept || reqHeaders?.accept);
    const isSSERequest = (typeof reqUrl === 'string' && /\/stream\//.test(reqUrl)) ||
                         (acceptHeader && String(acceptHeader).toLowerCase().includes('text/event-stream'));

    console.error('🔥 Fetch failed completely:', {
      error,
      url: reqUrl,
      method: (args[1] as any)?.method || 'GET',
      headers: (args[1] as any)?.headers,
      timestamp: new Date().toISOString(),
      errorMessage: error instanceof Error ? error.message : String(error),
      errorType: error instanceof TypeError ? 'TypeError' : error?.constructor?.name,
      isSSERequest
    });
    errorMessage = error instanceof Error ? error.message : String(error);

    // If this was an AbortError (e.g., caller aborted a long-lived SSE fetch),
    // rethrow so upstream logic (like fetchWithRetry) can handle it appropriately
    if (error instanceof Error && error.name === 'AbortError') {
      // Never convert aborts to synthetic responses for SSE; this prevents
      // retry loops with an already-aborted signal
      throw error;
    }

    // For all other network errors, return a synthetic 503 Response for uniform handling
    response = new Response(JSON.stringify({ 
      error: errorMessage,
      type: 'network_failure',
      timestamp: new Date().toISOString()
    }), {
      status: 503, // Service Unavailable - valid HTTP status
      statusText: 'Network Error - Service Unavailable'
    });
  }
  
  const duration = Date.now() - startTime;
  console.log(`Duration: ${duration}ms`);
  console.groupEnd();
  
  // Store debug entry
  const debugEntry: DebugEntry = {
    timestamp: new Date().toISOString(),
    method: requestMethod,
    url: requestUrl,
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.entries()),
    requestBody,
    responseBody,
    error: errorMessage,
    duration
  };
  
  debugHistory.unshift(debugEntry);
  if (debugHistory.length > MAX_DEBUG_ENTRIES) {
    debugHistory.length = MAX_DEBUG_ENTRIES;
  }
  
  // Alert on 500 errors
  if (response.status === 500) {
    console.error('='.repeat(80));
    console.error('500 ERROR DETAILS:');
    console.error('URL:', requestUrl);
    console.error('Method:', requestMethod);
    console.error('Request Body:', requestBody);
    console.error('Response Body:', responseBody);
    console.error('Error:', errorMessage);
    console.error('Check window.__DEBUG_HISTORY__ for full history');
    console.error('='.repeat(80));
  }
  
  return response;
};

// Function to check token expiration proactively
export function checkTokenExpiration(token: string | null): boolean {
  if (!token) return true;
  
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length === 3) {
      const payload = JSON.parse(atob(tokenParts[1]));
      const isExpired = payload.exp ? Date.now() > payload.exp * 1000 : false;
      
      if (isExpired) {
        console.log('⏰ Token has expired:', {
          expiredAt: new Date(payload.exp * 1000).toISOString(),
          currentTime: new Date().toISOString()
        });
        handleTokenExpired();
        return true;
      }
    }
  } catch (e) {
    console.error('Error checking token expiration:', e);
  }
  
  return false;
}
